import { RotateCcw } from "lucide-react";
import type { Organization } from "../types";
import { useLocale } from "../../../context/LanguageContext";
import { MutationErrorBanner } from "../../../components/ui/MutationErrorBanner";

interface ReactivateConfirmDialogProps {
  organization: Organization;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string | null;
}

export function ReactivateConfirmDialog({
  organization,
  onConfirm,
  onCancel,
  loading,
  error,
}: ReactivateConfirmDialogProps) {
  const { t } = useLocale();
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onCancel} />
      <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-success/10 p-2">
            <RotateCcw className="h-5 w-5 text-success" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground">
              {t("organizations.reactivate.title")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("organizations.reactivate.intro", { name: organization.name })}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              <strong>{t("organizations.reactivate.noteLabel")}</strong>{" "}
              {t("organizations.reactivate.noteBody")}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4">
            <MutationErrorBanner message={error} />
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 rounded-md px-4 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="h-9 rounded-md bg-success px-4 text-sm font-medium text-white hover:bg-success/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? t("organizations.reactivate.submitting") : t("organizations.reactivate.confirm")}
          </button>
        </div>
      </div>
    </>
  );
}
